import {setApplicationConfig} from '../applicationConfig';
import {config} from '../config';
import {CALL_TYPE, SHADES} from '../constants';
import {logger} from '../logger';
import type {TestIdValue} from '../types';
import {getClientCallType} from '../utils/getClientCallType';
import {parseCallMode} from '../utils/parseCallMode';
import {transformHiddenFunctionalityArrayToObject} from '../utils/transformHiddenFunctionalityArrayToObject';

import {DEFAULT, getBrandingPath, loadBranding} from './loadBranding';
import {setLanguages} from './setLanguages';
import type {Favicon, Manifest} from './types';

const setBrandApplicationConfig = (
    brandingApplicationConfig: Manifest['applicationConfig'] = {},
) => {
    for (const [key, value] of Object.entries(brandingApplicationConfig)) {
        setApplicationConfig(
            key as keyof Manifest['applicationConfig'],
            key === 'hiddenFunctionality'
                ? transformHiddenFunctionalityArrayToObject(
                      value as TestIdValue[],
                  )
                : value,
        );
    }
};

const setBrandDefaultUserConfig = (
    defaultUserConfig: Manifest['defaultUserConfig'] = {},
) => {
    for (const [k, value] of Object.entries(defaultUserConfig)) {
        const key = k as keyof Manifest['defaultUserConfig'];
        // Apply only for unchanged defaults
        if (config.isDefaultValue(key)) {
            if (key === 'callType' && typeof value === 'string') {
                config.set({key, value: getClientCallType(value) ?? CALL_TYPE});
                continue;
            }
            if (key === 'callMode') {
                const callMode = parseCallMode(value);
                if (callMode) {
                    config.set({key, value: callMode});
                } else {
                    logger.warn(
                        {callMode: value},
                        'Ignoring an unknown branding call mode',
                    );
                }
                continue;
            }
            config.set({key, value});
        }
    }
};

const setBrandPalette = (colorPalette: Manifest['colorPalette']) => {
    if (colorPalette && colorPalette.length > 0) {
        if (colorPalette.length !== SHADES.length) {
            logger.warn(
                'color palette has too many/few elements. check your branding manifest version.',
            );
        }
        colorPalette.forEach((color, idx) => {
            document.documentElement.style.setProperty(
                `--color-brand-${SHADES[idx]}`,
                color,
            );
        });
    } else {
        for (const shade of SHADES) {
            document.documentElement.style.removeProperty(
                `--color-brand-${shade}`,
            );
        }
    }
};

const setBrandBgColor = (backgroundColor: Manifest['backgroundColor']) => {
    if (backgroundColor) {
        document.documentElement.style.setProperty(
            '--color-brand-background',
            backgroundColor,
        );
    } else {
        document.documentElement.style.removeProperty(
            '--color-brand-background',
        );
    }
};

const setBrandApplicationTitle = (appTitle: Manifest['appTitle']) => {
    if (appTitle) {
        document.title = appTitle;
    }
};

const setBgReplacementAssets = (
    bgImageAssets: Manifest['applicationConfig']['bgImageAssets'],
    backgroundImageUrl: Manifest['defaultUserConfig']['bgImageUrl'],
) => {
    // Normalization of the old brands, makes sure we always have bgImageAssets if bgImageUrl is specified
    if (backgroundImageUrl && !bgImageAssets) {
        setApplicationConfig('bgImageAssets', [backgroundImageUrl]);
    }
};

const setFavicon = (favicon?: Favicon) => {
    if (!favicon || !('href' in favicon)) {
        return;
    }

    // Remove all existing favicon and apple-touch-icon links
    document
        .querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]')
        .forEach(el => {
            el.remove();
        });

    const link = document.createElement('link');
    link.rel = 'icon';
    if (favicon.type) {
        link.type = favicon.type;
    }
    if (favicon.sizes) {
        link.sizes.add(favicon.sizes);
    }
    link.href = getBrandingPath(favicon.href);
    document.head.appendChild(link);
};

const normalizePlugins = (brand: Manifest) => {
    if (!brand.plugins) {
        return;
    }
    const plugins: NonNullable<Manifest['plugins']> = [];
    for (const plugin of brand.plugins) {
        if (!plugin.src) {
            logger.error({plugin}, 'A Plugin must have a "src"');
            continue;
        }
        if (plugins.find(p => plugin.id && plugin.id === p.id)) {
            logger.error(
                {plugin},
                'Plugin with duplicate id found in branding manifest.',
            );
            continue;
        }
        const src = plugin.src;
        try {
            new URL(src);
            plugins.push({...plugin, src});
        } catch {
            plugins.push({...plugin, src: getBrandingPath(src)});
        }
    }
    brand.plugins = plugins;
};

const applyBranding = (brand: Manifest) => {
    // If backgroundBlurAmountLow is not set, use backgroundBlurAmount
    // to make sure we are not overriding the blur amount for old brands
    // that only have backgroundBlurAmount defined
    if (
        !brand.applicationConfig?.backgroundBlurAmountLow &&
        brand.defaultUserConfig?.backgroundBlurAmount
    ) {
        setApplicationConfig(
            'backgroundBlurAmountLow',
            brand.defaultUserConfig.backgroundBlurAmount,
        );
    }

    setBrandApplicationConfig(brand.applicationConfig);
    setFavicon(brand.favicon);
    setBrandDefaultUserConfig(brand.defaultUserConfig);
    setBrandApplicationTitle(brand.appTitle);
    setBrandPalette(brand.colorPalette);
    setBrandBgColor(brand.backgroundColor);
    setBgReplacementAssets(
        brand.applicationConfig?.bgImageAssets,
        brand.defaultUserConfig?.bgImageUrl,
    );
    setLanguages(brand.availableLanguages, brand.translations);
};

const fetchBranding = async (): Promise<Manifest> => {
    try {
        const brand = await loadBranding();
        normalizePlugins(brand);
        return brand;
    } catch (error: unknown) {
        logger.warn({error}, 'failed to load branding');
        return DEFAULT;
    }
};

const tryApplyBranding = (brand: Manifest) => {
    try {
        applyBranding(brand);
        return true;
    } catch (error: unknown) {
        logger.error({error}, 'branding manifest is invalid');
        return false;
    }
};

/**
 * Loads the deploy-time branding manifest and applies it to the global
 * application state: application and user config, document styles, favicon,
 * title, and languages.
 *
 * The returned promise never rejects. A manifest that fails to load or apply
 * is replaced by {@link DEFAULT}. Create it once, outside React rendering, and
 * read it with `use` so that no component renders before branding is applied.
 *
 * @returns The applied manifest.
 *
 * @example
 * const branding = bootstrapBranding();
 * root.render(<App infinity={infinity} branding={branding} />);
 */
export async function bootstrapBranding(): Promise<Manifest> {
    const brand = await fetchBranding();
    if (tryApplyBranding(brand) || brand === DEFAULT) {
        return brand;
    }
    tryApplyBranding(DEFAULT);
    return DEFAULT;
}
